import { CapitalOverview } from "@adyen/adyen-platform-experience-web";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import useCurrentUserAccount from "../../hooks/data/queries/useCurrentUserAccount";
import Skeleton from "../../components/Skeleton";
import Error from "../../components/Error";
import image1 from "../../assets/capitals/image1.png";
import image2 from "../../assets/capitals/image2.png";
import image3 from "../../assets/capitals/image3.png";
import image4 from "../../assets/capitals/image4.png";
import { getCapitalOffers } from "../../services/accountService";
import { convertToAdyenNumber } from "../../utils/number";
import { formatAmount } from "../../utils/currency";
import { getAdyenPlatformExperience } from "../../services/adyenServices";
import useAccountConfigQuery from "../../hooks/data/queries/useAccountConfigQuery";
import Button from "../../components/Button";
import { ArrowLeftIcon } from "@heroicons/react/24/solid";

type CapitalParams = { accountHolderCode: string };

const ideas = [
  {
    title: "Order costumes",
    subtitle: "for competitive and recreational classes well in advance.",
    image: image1,
  },
  {
    title: "Expand your class schedule",
    subtitle: "with better lighting, costumes, and staging.",
    image: image2,
  },
  {
    title: "Upgrade your studio space",
    subtitle: "with new mirrors, floors, barres, or sound systems.",
    image: image3,
  },
  {
    title: "Enhance recital productions",
    subtitle: "with better lighting, costumes and staging.",
    image: image4,
  },
];

function Capital(): JSX.Element {
  const { accountHolderCode } = useParams<CapitalParams>();
  const { data: currentUserAccountData } = useCurrentUserAccount();
  const { data: accountConfigData } = useAccountConfigQuery(accountHolderCode, {
    enabled: currentUserAccountData?.type !== "CSR",
  });
  // const [headerText, setHeaderText] = useState("");
  // const [isLoadingAmount, setIsLoadingAmount] = useState(false);
  const capitalOverviewRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const isPendingOrRestricted = ["Pending", "Restricted"].includes(
    accountConfigData?.merchantStatus ?? ""
  );

  const showComponent =
    currentUserAccountData?.type === "CSR" ||
    accountHolderCode === currentUserAccountData?.accountHolderCode;

  async function mountCapitalComponent(node: HTMLElement) {
    const capitalOverview = new CapitalOverview({
      core: await getAdyenPlatformExperience(accountHolderCode || "", [
        "Capital Component: Manage",
      ]),
    });
    capitalOverview.mount(node);
  }

  /* async function getCapitalAmount() {
    setIsLoadingAmount(true);
    const capitalOffer = await getCapitalOffers(accountHolderCode ?? "");
    const value = capitalOffer.Amount.Value;
    setHeaderText(getHeaderText(value));
    setIsLoadingAmount(false);
  }

  function getHeaderText(amount: number) {
    if (amount > 0) {
      const formattedAmount = new Intl.NumberFormat("en-US").format(
        Math.round(amount / 100)
      );
      return `Unlock up to $${formattedAmount} in pre-approved funding to elevate your studio!`;
    } else if (amount === 0) {
      return "Your offer is coming soon, keep an eye out!";
    } else if (amount === -1) {
      return "Congrats on unlocking funding!";
    }
    return "";
  }

  useEffect(() => {
    getCapitalAmount();
  }, []); */

  useEffect(() => {
    mountCapitalComponent(capitalOverviewRef.current as HTMLElement);
  });

  if (!showComponent) return <></>;

  return (
    <>
      {isPendingOrRestricted ? (
        <Error message="You're not allowed to view this page." />
      ) : (
        <div className="relative -top-4 flex min-h-screen w-full px-0">
          <div className="mt-5">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate(-1)}
              icon={<ArrowLeftIcon />}
            >
              Return
            </Button>
          </div>

          <div className="flex w-1/2 flex-col pl-24 pt-8">
            <h3 className="mb-3 w-10/12 text-3xl font-bold">
              Unlock funding to elevate your studio!
              {/* isLoadingAmount ? (
                <div className="flex flex-col gap-y-2">
                  <Skeleton className="w-full" />
                  <Skeleton className="w-full" />
                </div>
              ) : (
                headerText
              )*/}
            </h3>
            <p className="w-10/12 text-sm font-semibold text-gray-500">
              Say goodbye to complex, time-consuming paperwork and long wait
              times. <br /> With Capital, getting business financing has never
              been simpler.
            </p>
            <div ref={capitalOverviewRef} className="my-10" />
            <div>
              <h4 className="mb-2 text-xl font-semibold">
                Business financing as easy as 1, 2, 3
              </h4>
              <ol className="ml-4 mb-16 list-decimal">
                <li>
                  Choose your amount: select your desired capital from your
                  pre-approved offers.
                </li>
                <li>
                  Access your funds: accept your offer and get paid the same
                  business day.
                </li>
                <li>
                  Repay automatically: repay automatically as a fixed percentage
                  of your daily sales.
                </li>
              </ol>
            </div>
            <p className="text-sm font-semibold text-gray-400">
              *Get paid the same business day
            </p>
          </div>
          <div className="flex w-1/2 flex-col bg-gray-50 p-8 pt-8 pr-24">
            <h5 className="pb-4 font-semibold">
              Here are some ideas on how to use the funding:
            </h5>
            <div className="flex flex-col gap-y-6 pb-4">
              {ideas.map((x, i) => (
                <div
                  key={x.title}
                  className="flex h-28 justify-between overflow-hidden rounded-3xl border border-sky-200 bg-sky-50"
                >
                  <div className="flex flex-col justify-center gap-y-2 p-6">
                    <div className="text-base font-semibold">{x.title}</div>
                    <div className="text-sm">{x.subtitle}</div>
                  </div>
                  <img
                    src={x.image}
                    alt=""
                    className="h-full w-52 object-cover"
                  />
                </div>
              ))}
            </div>
            <div className="self-end font-semibold text-gray-500">
              ... and so much more!
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Capital;
